import React from 'react';
import { ImagePreview } from '@/components';
import { Incident } from '@/services/api/incidents';

interface IncidentDetailContentProps {
  incident: Incident;
}

const FieldDisplay: React.FC<{
  label: string;
  value: string | React.ReactNode;
  className?: string;
}> = ({ label, value, className = '' }) => (
  <div className={`flex flex-col ${className}`}>
    <label 
      className="block mb-1 text-xs font-medium tracking-wide"
      style={{ color: 'var(--text-soft-400, #A3A3A3)' }}
    >
      {label}
    </label>
    <div className="text-sm font-medium text-gray-900 break-words whitespace-pre-wrap">
      {value || '-'}
    </div>
  </div>
);

const Row: React.FC<{
  children: React.ReactNode;
  hasBorder?: boolean;
  columns?: 1 | 2;
}> = ({ children, hasBorder = true, columns = 1 }) => (
  <div
    className={`w-full ${hasBorder ? 'pb-3 mb-3 border-b' : 'pb-5'} ${
      columns === 2 ? 'grid grid-cols-2 gap-4' : ''
    }`}
    style={hasBorder ? { borderColor: '#EBEBEB' } : {}}
  >
    {children}
  </div>
);

const SectionHeader: React.FC<{ title: string; isFirst?: boolean }> = ({ title }) => (
  <div className="bg-gray-50 px-6 py-2 mb-4">
    <div className="text-xs font-medium tracking-wide text-gray-400 uppercase">
      {title}
    </div>
  </div>
);

const INCIDENT_TYPE_MAP: Record<string, string> = {
  ACCIDENT_DAMAGE: 'Kerusakan akibat Kecelakaan',
  USAGE_DAMAGE: 'Kerusakan akibat Penggunaan',
  LOST_UNIT: 'Kehilangan Unit',
  MINOR_DAMAGE: 'Kerusakan Ringan',
};

const formatMitraDisplay = (driverCode?: string, driverName?: string) => {
  if (driverCode && driverName) return `${driverCode} - ${driverName}`;
  return driverCode || driverName || '-';
};

const getCostResponsibilityDisplay = (incident: Incident) => {
  switch (String(incident.costResponsibility || '').toUpperCase()) {
    case 'OEM':
      return {
        primaryValue: 'OEM',
        secondaryLabel: 'OEM Name',
        secondaryValue: incident.oemName || '-',
      };
    case 'CLIENT':
      return {
        primaryValue: 'Client',
        secondaryLabel: 'Client Name',
        secondaryValue: incident.clientName || '-',
      };
    case 'MITRA':
      return {
        primaryValue: 'Mitra',
        secondaryLabel: 'Mitra',
        secondaryValue: formatMitraDisplay(incident.driverCode, incident.driverName),
      };
    case 'DASH_ELECTRIC':
      return {
        primaryValue: 'Dash Electric',
        secondaryLabel: '',
        secondaryValue: '',
      };
    default:
      return {
        primaryValue: incident.costResponsibility || '-',
        secondaryLabel: '',
        secondaryValue: '',
      };
  }
};

const IncidentDetailContent: React.FC<IncidentDetailContentProps> = ({ incident }) => {
  const damageImageURL = incident.damageImageURL;
  const policeReportHref = incident.policeReportURL
    ? incident.policeReportURL.startsWith('http')
      ? incident.policeReportURL
      : `https://${incident.policeReportURL}`
    : '';
  const costResponsibilityDisplay = getCostResponsibilityDisplay(incident);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
      <div className="px-6 py-4">
        <div className="text-base font-semibold text-gray-900">
          Incident Report
        </div>
      </div>

      <SectionHeader title="Vehicle" isFirst={true} />
      <div className="px-6">
        <Row>
          <FieldDisplay
            label="Plate Number"
            value={incident.platNomor}
          />
        </Row>
        <Row columns={2} hasBorder={false}>
          <FieldDisplay label="OEM" value={incident.oemName} />
          <FieldDisplay label="Models" value={incident.modelName} />
        </Row>
      </div>

      <SectionHeader title="Mitra" />
      <div className="px-6">
        <Row columns={2}>
          <FieldDisplay label="MTRCode" value={incident.driverCode} />
          <FieldDisplay label="Mitra Name" value={incident.driverName} />
        </Row>
        <Row columns={2} hasBorder={false}>
          <FieldDisplay label="Client" value={incident.clientName || '-'} />
          <FieldDisplay label="Pitstop" value={incident.hubName || '-'} />
        </Row>
      </div>

      <SectionHeader title="Incident Detail" />
      <div className="px-6">
        <Row>
          <FieldDisplay
            label="Severity"
            value={
              incident.severity ? (
                <span className="inline-flex items-center gap-1.5 rounded border border-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">
                  <span
                    className={`h-2 w-2 rounded-full ${
                      incident.severity === 'LOW'
                        ? 'bg-green-500'
                        : incident.severity === 'MEDIUM'
                        ? 'bg-blue-500'
                        : incident.severity === 'HIGH'
                        ? 'bg-orange-500'
                        : incident.severity === 'CRITICAL'
                        ? 'bg-red-500'
                        : 'bg-gray-400'
                    }`}
                  />
                  {incident.severity}
                </span>
              ) : '-'
            }
          />
        </Row>
        <Row>
          <FieldDisplay
            label="Incident Type"
            value={INCIDENT_TYPE_MAP[incident.incidentType] || incident.incidentType}
          />
        </Row>
        <Row>
          <FieldDisplay
            label="Chronology / Description"
            value={incident.chronology}
          />
        </Row>
        <Row>
          <FieldDisplay
            label="Foto Kerusakan Kendaraan"
            value={
              damageImageURL ? (
                <div className="mt-2 w-full max-w-[557px] h-[200px] rounded overflow-hidden mx-auto">
                  <ImagePreview
                    src={damageImageURL}
                    alt="Vehicle Damage"
                    className="w-full h-48 rounded object-contain bg-white"
                  />
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center text-gray-400 w-full max-w-[557px] h-[200px] mt-2 mx-auto">
                  <i className="ri-image-line text-2xl mb-1"></i>
                  <span className="text-xs">No image uploaded</span>
                </div>
              )
            }
          />
        </Row>
        <Row hasBorder={false}>
          <FieldDisplay
            label="Laporan Polisi (PDF)"
            value={
              incident.policeReportURL ? (
                <a
                  href={policeReportHref}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 block break-words font-medium text-sm text-purple-600 hover:underline"
                >
                  {incident.policeReportURL}
                </a>
              ) : (
                '-'
              )
            }
          />
        </Row>
      </div>

      <SectionHeader title="Financial & Resolution" />
      <div className="px-6 pb-6">
        <Row
          columns={costResponsibilityDisplay.secondaryLabel ? 2 : 1}
          hasBorder={false}
        >
          <FieldDisplay
            label="Biaya Ditanggung"
            value={costResponsibilityDisplay.primaryValue}
          />
          {costResponsibilityDisplay.secondaryLabel && (
            <FieldDisplay
              label={costResponsibilityDisplay.secondaryLabel}
              value={costResponsibilityDisplay.secondaryValue}
            />
          )}
        </Row>
      </div>
    </div>
  );
};

export default IncidentDetailContent;
